import { Inject, Injectable, Injector } from '@angular/core'
import { Observable, Subscription } from 'rxjs'
import { CmsService } from '@spartacus/core'
import { filter, map, switchMap, withLatestFrom } from 'rxjs/operators'
import { MbCaasService } from '../mb-caas/mb-caas.service'
import { MbContentPageMetaResolver } from '../../page/mb-content-page-meta-resolver'
import { CmsDOMInputParser } from '../../../shared/utils/cms-dom-input-parser'
import { CampusModalData } from '../../components/shared/campus-modal/campus-modal.component'
import { CaasIdType } from '../../enums/caas-id-types'
import { CookieService } from 'ngx-cookie-service'
import { ModalSize } from '../../enums/modal-size'
import { MbModalService } from '../../../shared/services/modal.service'
import { MbConstants } from '../../../shared/mb-constants'
import { CookieKeys } from '../../enums/cookie-keys'
import { CmsComponentUtils } from '../../components/utils/cms-component-utils'
import { NavigationEnd, Router } from '@angular/router'
import { CmsEnvironmentService } from '../cms-environment.service'

@Injectable({
  providedIn: 'root'
})
export class MbCampusModalService {

  subscription = new Subscription()

  constructor (private cmsService: CmsService,
               private cmsEnvService: CmsEnvironmentService,
               private router: Router,
               private mbCaasService: MbCaasService,
               private mbContentPageMetaResolver: MbContentPageMetaResolver,
               private cmsDOMInputParser: CmsDOMInputParser,
               @Inject(Injector) private readonly injector: Injector,
               private cookieService: CookieService) {
    this.init()
  }

  private get modalService (): MbModalService {
    return this.injector.get(MbModalService)
  }

  init (): void {
    this.subscription.add(
      this.router.events?.pipe(
        filter(event => event instanceof NavigationEnd),
        switchMap(() => this.resolvePageSEO()),
        filter(seoUrl => seoUrl.includes(MbConstants.MILTENYI_UNIVERSITY_HOME_PAGE_SEO)),
        switchMap(() => this.loadCampusModal()),
        withLatestFrom(this.cmsEnvService.isFirstSpiritPreview$)
      ).subscribe(([modal, isFSPreview]) => {
        if (!this.cookieService.check(CookieKeys.CAMPUS_MODAL)
          || this.cookieService.get(CookieKeys.CAMPUS_MODAL) !== MbConstants.CAMPUS_MODAL_COOKIE) {
          const campusModal = this.mapCampusModalContent(modal)
          if (CmsComponentUtils.isComponentVisible(campusModal.visibility, isFSPreview)) {
            this.cookieService.set(CookieKeys.CAMPUS_MODAL, MbConstants.CAMPUS_MODAL_COOKIE, { path: '/' })
            this.modalService.openCampusModal(
              campusModal.id,
              campusModal.headline,
              campusModal.description,
              campusModal.buttonText,
              campusModal.modalSize,
              campusModal.flexType)
          }
        }
      }))
  }

  private resolvePageSEO (): Observable<string> {
    return this.cmsService.getCurrentPage().pipe(
      map(page => page?.properties?.formData?.pt_seoUrl?.value ?? '')
    )
  }

  private loadCampusModal (): Observable<any[]> {
    return this.mbCaasService.getByIds([MbConstants.CAMPUS_MODAL_TEMPLATE], CaasIdType.SEOURL)
  }

  private mapCampusModalContent (page: any[]): CampusModalData {
    const id = page[0]?.page?.children[1]?.children[0]?.identifier ?? ''
    const flexType = page[0]?.page?.children[1]?.children[0]?.name ?? ''
    const formData = page[0]?.page?.children[1]?.children[0]?.formData ?? {}
    return {
      id: id ?? '',
      headline: {
        name: formData.st_headline?.name ?? '',
        value: this.cmsDOMInputParser.parse(formData.st_headline?.value) ?? ''
      },
      description: {
        name: formData.st_text?.name ?? '',
        value: this.cmsDOMInputParser.parse(formData.st_text?.value) ?? ''
      },
      buttonText: this.cmsDOMInputParser.parse(formData.st_button_text?.value) ?? '',
      modalSize: ModalSize.XL,
      flexType: flexType,
      visibility: {
        hide: formData.st_hide_component?.value ?? false,
        hideBefore: formData.st_hide_before_date?.value ?? '',
        hideAfter: formData.st_hide_after_date?.value ?? ''
      }
    } as CampusModalData
  }

}
